import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Switch, Row, Col, Upload, Button, Typography, Tag, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import moment from 'moment';
import { createUser, updateUser, getCompanyDepartments, uploadUserAvatar, assignUserTextingNumber } from '../api';
import { getProducts } from '../../../reducers/products/resources';
import { getUserAvatar } from '../../../reducers/user/resources';
import { getVonageNumbersAvailableList } from '../../../reducers/nexmo/resources';
import UserAvatar from '../../common/UserAvatar';
import PhoneDisplay from '../../common/PhoneDisplay';
import PhoneInputUS from '../../common/PhoneInputUS';
import { avatarPresetNames, getPresetAvatarSrc } from '../../../utils/avatar';
import { phone as phoneLib } from 'phone';
import config from '../../../config';

const { Text } = Typography;

const ROLE_OPTIONS = [
    { label: 'GoHire Admin', value: config.userRoles.ghAdmin },
    { label: 'Company Admin', value: config.userRoles.companyAdmin },
    { label: 'User', value: config.userRoles.user },
];

const getSvgSrc = (name) => {
    try {
        const imported = require(`../../../assets/avatars/${name}.svg`);
        return typeof imported === 'string' ? imported : imported.default || imported;
    } catch {
        return '';
    }
};

const presetAvatars = avatarPresetNames.map(name => ({
    name,
    src: getSvgSrc(name),
}));

function normalizeMobileValue(raw) {
    if (typeof raw === 'object' && raw !== null) {
        const { countryCode, areaCode, phoneNumber } = raw;
        if (!areaCode && !phoneNumber) return '';
        return `+${countryCode || 1}${areaCode || ''}${phoneNumber || ''}`;
    }
    return typeof raw === 'string' ? raw : '';
}

// Pool-number picker rows. `last_verified` comes from the available-list
// endpoint as 'YYYY-MM-DD' or the literal 'Never'. Same date format as the
// Vonage Numbers page (MM/DD/YYYY).
const formatVerifiedDate = (value) => {
    const d = moment(value);
    return d.isValid() ? d.format('MM/DD/YYYY') : value;
};

const VerificationTag = ({ lastVerified }) => (lastVerified
    ? <Tag color="success" style={{ marginInlineEnd: 0 }}>Verified {formatVerifiedDate(lastVerified)}</Tag>
    : <Tag style={{ marginInlineEnd: 0 }}>Never verified</Tag>);

const PoolNumberRow = ({ mobile, lastVerified }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <PhoneDisplay value={mobile} />
        <VerificationTag lastVerified={lastVerified} />
    </div>
);

const mobileValidator = (_, value) => {
    const mobile = normalizeMobileValue(value);
    if (!mobile) return Promise.resolve();
    const result = phoneLib(mobile);
    if (!result.isValid) return Promise.reject('Please enter a valid mobile number');
    return Promise.resolve();
};

const UserModal = ({ open, user, companyId, onClose, onSuccess, isGhAdmin = false }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [deptOptions, setDeptOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    // 1-1 texting number picker (gh-admin, edit mode only)
    const [poolNumbers, setPoolNumbers] = useState([]);
    const [poolLoading, setPoolLoading] = useState(false);
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [avatarPreset, setAvatarPreset] = useState(null);
    const [showPresets, setShowPresets] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [currentAvatarUrl, setCurrentAvatarUrl] = useState(null);
    const isEdit = !!user;

    // Fetch department options when modal opens
    useEffect(() => {
        if (open && companyId) {
            getCompanyDepartments(companyId)
                .then((res) => {
                    const depts = res.data || [];
                    setDeptOptions(depts.map((d) => ({ label: d.name, value: d.id })));
                })
                .catch(() => setDeptOptions([]));
        }
    }, [open, companyId]);

    // Fetch product options when modal opens
    useEffect(() => {
        if (open) {
            getProducts()
                .then((res) => {
                    const products = res.data || [];
                    setProductOptions(products.map((p) => ({ label: p.name, value: p.id })));
                })
                .catch(() => setProductOptions([]));
        }
    }, [open]);

    // Fetch assignable pool numbers for the 1-1 texting number picker.
    // Rows come from GET /api/v1/numbers/vonage/list/available: pool numbers
    // whose latest status is New/Released and that no botmap uses yet.
    const showTextingNumber = open && isEdit && isGhAdmin;
    useEffect(() => {
        if (!showTextingNumber) return;
        setPoolLoading(true);
        getVonageNumbersAvailableList()
            .then((res) => {
                const rows = Array.isArray(res) ? res : (res?.data || []);
                const options = rows.map((n) => {
                    const lastVerified = n.last_verified && n.last_verified !== 'Never' ? n.last_verified : null;
                    return {
                        value: n.id,
                        mobile: n.mobile,
                        lastVerified,
                        // plain-text mirror of the row for the search box
                        searchText: `${n.mobile} ${lastVerified ? `verified ${lastVerified}` : 'never verified'}`.toLowerCase(),
                    };
                });
                // Numbers with a verification history first, most recent first.
                options.sort((a, b) => {
                    if (!!a.lastVerified !== !!b.lastVerified) return a.lastVerified ? -1 : 1;
                    if (a.lastVerified && b.lastVerified) return b.lastVerified.localeCompare(a.lastVerified);
                    return a.mobile.localeCompare(b.mobile);
                });
                setPoolNumbers(options);
            })
            .catch(() => setPoolNumbers([]))
            .finally(() => setPoolLoading(false));
    }, [showTextingNumber]);

    // Populate form and avatar when editing
    useEffect(() => {
        if (open) {
            setAvatarFile(null);
            setAvatarPreview(null);
            setAvatarPreset(null);
            setShowPresets(false);
            if (user) {
                form.setFieldsValue({
                    targetNumber: undefined,
                    name: user.name,
                    username: user.username,
                    email: user.email,
                    mobile: user.mobile || '',
                    department_id: user.department_id,
                    product_id: user.product_id,
                    role: user.role,
                    job_title: user.job_title || '',
                    active: user.active,
                    desktop_notification: user.desktop_notification !== false,
                    email_notification: user.email_notification !== false,
                    sms_notification: user.sms_notification !== false,
                });
                // Load current avatar
                setCurrentAvatarUrl(null);
                getUserAvatar(user.id)
                    .then((res) => {
                        const url = res.avatar_url || res.data?.avatar_url;
                        if (url) setCurrentAvatarUrl(url);
                    })
                    .catch(() => {});
            } else {
                form.resetFields();
                setCurrentAvatarUrl(null);
            }
        }
    }, [open, user, form]);

    const handleAvatarCrop = (file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const img = new Image();
            img.src = reader.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const size = 124;
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, size, size);
                canvas.toBlob((blob) => {
                    const resized = new File([blob], file.name, { type: 'image/png' });
                    setAvatarFile(resized);
                    setAvatarPreview(URL.createObjectURL(resized));
                    setAvatarPreset(null);
                }, 'image/png', 1);
            };
        };
        return false;
    };

    const handlePresetSelect = (name) => {
        setAvatarPreset(name);
        setAvatarFile(null);
        setAvatarPreview(null);
    };

    const uploadAvatarForUser = async (userId) => {
        if (avatarPreset) {
            const formData = new FormData();
            formData.append('avatar_url', avatarPreset);
            formData.append('avatar_type', 'preset');
            await uploadUserAvatar(userId, formData);
        } else if (avatarFile) {
            const formData = new FormData();
            formData.append('file', avatarFile);
            await uploadUserAvatar(userId, formData);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            const mobileNormalized = normalizeMobileValue(values.mobile);

            if (isEdit) {
                const body = {
                    name: values.name,
                    username: values.username,
                    email: values.email,
                    mobile: mobileNormalized,
                    department_id: values.department_id,
                    product_id: values.product_id,
                    role: values.role,
                    job_title: values.job_title || '',
                    active: !!values.active,
                    desktop_notification: !!values.desktop_notification,
                    email_notification: !!values.email_notification,
                    sms_notification: !!values.sms_notification,
                };
                if (values.password) body.password = values.password;
                await updateUser(companyId, user.id, body);

                // Assign the 1-1 texting number (gh-admin only). Non-fatal: the
                // user row is already saved, so report and still refresh.
                let numberAssigned = false;
                let numberError = null;
                if (isGhAdmin && values.targetNumber) {
                    try {
                        await assignUserTextingNumber(companyId, user.id, values.targetNumber);
                        numberAssigned = true;
                    } catch (e) {
                        numberError = e.response?.data?.message || e.message || 'unknown error';
                    }
                }

                // Upload avatar if changed
                if (avatarFile || avatarPreset) {
                    try {
                        await uploadAvatarForUser(user.id);
                    } catch {
                        message.warning('User saved but avatar upload failed');
                    }
                }

                if (numberError) {
                    message.warning(`User saved but texting number was not assigned: ${numberError}`);
                } else {
                    message.success(numberAssigned ? 'User updated · texting number assigned' : 'User updated');
                }
            } else {
                const body = {
                    name: values.name,
                    username: values.username,
                    password: values.password,
                    email: values.email,
                    mobile: mobileNormalized,
                    department_id: values.department_id,
                    product_id: values.product_id,
                    role: values.role,
                    job_title: values.job_title || '',
                    active: !!values.active,
                    desktop_notification: !!values.desktop_notification,
                    email_notification: !!values.email_notification,
                    sms_notification: !!values.sms_notification,
                };
                const res = await createUser(companyId, body);
                const newUser = res.data || res;

                // Upload avatar if selected
                if ((avatarFile || avatarPreset) && newUser?.id) {
                    try {
                        await uploadAvatarForUser(newUser.id);
                    } catch {
                        message.warning('User created but avatar upload failed');
                    }
                }

                message.success('User created');
            }
            onSuccess();
        } catch (err) {
            if (err.errorFields) return;
            message.error(err.message || 'Failed to save user');
        } finally {
            setSubmitting(false);
        }
    };

    // Determine what avatar to show
    const displayAvatarSrc = avatarPreview
        || (avatarPreset ? getPresetAvatarSrc(avatarPreset) : null)
        || currentAvatarUrl
        || null;

    const formName = Form.useWatch('name', form);

    return (
        <Modal
            title={isEdit ? 'Edit User' : 'Add User'}
            open={open}
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={submitting}
            destroyOnClose
            width={720}
        >
            {/* ── Avatar Section ──────────────────────────────────── */}
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: 20,
                padding: 16,
                border: '1px solid #E6E6E6',
                borderRadius: 8,
                backgroundColor: '#FAFAFA',
            }}>
                <div style={{ marginRight: 16 }}>
                    <UserAvatar
                        name={formName || user?.name || ''}
                        src={displayAvatarSrc}
                        size={72}
                        showTooltip={false}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Profile Picture</Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                        Upload a photo or choose a preset avatar.
                    </Text>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <ImgCrop cropShape="round" showGrid={false} quality={1}>
                            <Upload
                                beforeUpload={handleAvatarCrop}
                                showUploadList={false}
                                accept="image/*"
                                disabled={uploading}
                            >
                                <Button size="small" icon={<UploadOutlined />} loading={uploading}>
                                    Upload
                                </Button>
                            </Upload>
                        </ImgCrop>
                        <Button size="small" onClick={() => setShowPresets(!showPresets)}>
                            {showPresets ? 'Hide Presets' : 'Presets'}
                        </Button>
                    </div>
                    {showPresets && (
                        <div style={{ marginTop: 12, borderTop: '1px solid #e6e6e6', paddingTop: 12 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 6 }}>
                                {presetAvatars.map(a => (
                                    <div
                                        key={a.name}
                                        onClick={() => handlePresetSelect(a.name)}
                                        style={{
                                            cursor: 'pointer',
                                            border: avatarPreset === a.name ? '2px solid #4CAF50' : '1px solid #E6E6E6',
                                            borderRadius: '50%',
                                            padding: 2,
                                            backgroundColor: '#fff',
                                            aspectRatio: '1/1',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <img
                                            src={a.src}
                                            alt={a.name}
                                            style={{ width: '100%', height: '100%', borderRadius: '50%', display: 'block', objectFit: 'cover' }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Form Fields (2-column) ──────────────────────────── */}
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    active: true,
                    desktop_notification: true,
                    email_notification: true,
                    sms_notification: true,
                    role: config.userRoles.user,
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Username is required' }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Valid email is required' }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="password" label="Password" rules={isEdit ? [] : [{ required: true, message: 'Password is required' }]}>
                            <Input.Password placeholder={isEdit ? 'Leave blank to keep current' : ''} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="department_id" label="Department" rules={[{ required: true, message: 'Department is required' }]}>
                            <Select options={deptOptions} placeholder="Select department" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="role" label="Role" rules={[{ required: true }]}>
                            <Select options={ROLE_OPTIONS} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="mobile" label="Mobile" rules={[{ validator: mobileValidator }]}>
                            <PhoneInputUS enableSearch />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="job_title" label="Job Title">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                {/* ── 1-1 Texting Number (gh-admin, edit only) ───────── */}
                {showTextingNumber && (
                    <div
                        data-testid="texting-number-panel"
                        style={{
                            marginBottom: 16,
                            padding: 12,
                            border: '1px solid #E6E6E6',
                            borderRadius: 8,
                            backgroundColor: '#FAFAFA',
                        }}
                    >
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>1-1 Texting Number</Text>
                        <Row gutter={16} align="top">
                            <Col span={10}>
                                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>Current</Text>
                                {user?.longcode_mobile
                                    ? <PhoneDisplay value={user.longcode_mobile} />
                                    : <Text type="secondary">None</Text>}
                            </Col>
                            <Col span={14}>
                                <Form.Item
                                    name="targetNumber"
                                    label={user?.longcode_mobile ? 'Change number' : 'Assign number'}
                                    style={{ marginBottom: 0 }}
                                    extra={user?.longcode_mobile
                                        ? 'The current number is returned to the Vonage pool. Its conversation history stays in the inbox.'
                                        : 'Picks an available number from the Vonage pool.'}
                                >
                                    <Select
                                        allowClear
                                        showSearch
                                        filterOption={(input, opt) => (opt?.searchText || '').includes(input.toLowerCase())}
                                        loading={poolLoading}
                                        placeholder="Keep current number"
                                        options={poolNumbers}
                                        popupClassName="gh-pool-number-dropdown"
                                        optionRender={(opt) => (
                                            <div
                                                data-verified={opt.data.lastVerified ? 'yes' : 'no'}
                                                style={opt.data.lastVerified ? {
                                                    // antd success tokens — same green family as the
                                                    // "Verified" tag on the Vonage Numbers page
                                                    backgroundColor: '#f6ffed',
                                                    borderLeft: '3px solid #b7eb8f',
                                                    margin: '-5px -12px',
                                                    padding: '5px 12px 5px 9px',
                                                } : { margin: '-5px -12px', padding: '5px 12px' }}
                                            >
                                                <PoolNumberRow mobile={opt.data.mobile} lastVerified={opt.data.lastVerified} />
                                            </div>
                                        )}
                                        labelRender={({ value }) => {
                                            const opt = poolNumbers.find((n) => n.value === value);
                                            return opt
                                                ? <PoolNumberRow mobile={opt.mobile} lastVerified={opt.lastVerified} />
                                                : value;
                                        }}
                                        notFoundContent={poolLoading ? 'Loading…' : 'No available numbers in the pool'}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                )}

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="product_id"
                            label="Product"
                            rules={[{ required: true, message: 'Product is required' }]}
                            extra="Sets the plan and default feature bundles. Fine-tune per user via Access."
                        >
                            <Select
                                options={productOptions}
                                placeholder="Select product"
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="active" label="Active" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="desktop_notification" label="Desktop" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="email_notification" label="Email Notif" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="sms_notification" label="SMS Notif" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default UserModal;
